/**
 * Empty State Component
 * Displayed when no data is available
 */
import React from 'react';
import { Package, Search, FolderOpen } from 'lucide-react';

import Button from '../Button/Button';

/**
 * Empty State Component
 * @param {Object} props - Component props
 * @param {string} props.title - Empty state title
 * @param {string} props.description - Empty state description
 * @param {React.ReactNode} props.icon - Custom icon
 * @param {string} props.iconType - Preset icon type (default, search, folder)
 * @param {string} props.actionLabel - Action button label
 * @param {Function} props.onAction - Action button handler
 * @returns {JSX.Element} Empty state element
 */
const EmptyState = ({
  title = 'No data found',
  description = 'There is no data to display at the moment.',
  icon,
  iconType = 'default',
  actionLabel,
  onAction,
}) => {
  const icons = {
    default: Package,
    search: Search,
    folder: FolderOpen,
  };

  const IconComponent = icon || icons[iconType] || Package;

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
        <IconComponent className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
        {title}
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mb-6">
        {description}
      </p>
      {actionLabel && onAction && (
        <Button onClick={onAction} variant="primary">
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

export default EmptyState;
